import { z } from 'zod'

/** Envelope lifecycle — PRD §6.7 (exactly 10 states). */
export const EnvelopeState = z.enum([
  'LOCAL_DRAFT',
  'PREPARING',
  'PREPARATION_FAILED',
  'ACTIVE',
  'AWAITING_FINALIZATION',
  'COMPLETED',
  'FINALIZATION_FAILED',
  'DECLINED',
  'VOIDED',
  'EXPIRED',
])
export type EnvelopeState = z.infer<typeof EnvelopeState>

/** Recipient progression — PRD §6.7. */
export const RecipientState = z.enum(['BLOCKED', 'ACTIVE', 'SUBMITTED', 'DECLINED'])
export type RecipientState = z.infer<typeof RecipientState>

/** Independent delivery observation — PRD §6.7. Never implies recipient submission. */
export const DeliveryState = z.enum([
  'NOT_MANAGED',
  'QUEUED',
  'DISPATCHING',
  'ACCEPTED',
  'DELIVERED',
  'BOUNCED',
  'COMPLAINED',
  'SUPPRESSED',
  'UNKNOWN',
])
export type DeliveryState = z.infer<typeof DeliveryState>

/** Artifact lifecycle independent of envelope state — PRD §6.7. */
export const ArtifactAvailability = z.enum([
  'STAGING',
  'AVAILABLE',
  'DELETING',
  'DELETED',
  'MISSING',
])
export type ArtifactAvailability = z.infer<typeof ArtifactAvailability>

/** Multi-signer routing — PRD §2.1, §6.1. */
export const RoutingMode = z.enum(['PARALLEL', 'SEQUENTIAL'])
export type RoutingMode = z.infer<typeof RoutingMode>

/** Who operates invitation delivery — PRD §2.1, §6.2/§6.3. */
export const DeliveryMode = z.enum(['MANUAL', 'MANAGED'])
export type DeliveryMode = z.infer<typeof DeliveryMode>

/** Sender participation relative to external signers — PRD §6.1. */
export const SenderOrder = z.enum(['BEFORE', 'AFTER'])
export type SenderOrder = z.infer<typeof SenderOrder>

/**
 * Field types — PRD §4.3 seven plus the adopted Part Three D7 catalogue
 * additions (`COMPANY`, `TITLE`). D7 makes both first-class field types with
 * distinct palette identities and text semantics; the §4.3 members keep their
 * names and meaning (S0.11, register §6/§8).
 */
export const FieldType = z.enum([
  'SIGNATURE',
  'INITIALS',
  'DATE_SIGNED',
  'FULL_NAME',
  'EMAIL',
  'TEXT',
  'CHECKBOX',
  'COMPANY',
  'TITLE',
  'FORMULA',
])
export type FieldType = z.infer<typeof FieldType>

/** Plan tier — PRD §2.1. */
export const PlanTier = z.enum(['FREE', 'LIFETIME'])
export type PlanTier = z.infer<typeof PlanTier>

/** Recipient/service message locale — PRD §10.3. Sender UI is English-only. */
export const LocaleCode = z.enum(['en', 'es'])
export type LocaleCode = z.infer<typeof LocaleCode>

/** Participant role on an envelope — PRD §6.1. */
export const ParticipantKind = z.enum(['signer', 'cc'])
export type ParticipantKind = z.infer<typeof ParticipantKind>

/** Signature capture method — PRD §4.4. */
export const SignatureMethod = z.enum(['draw', 'type', 'upload'])
export type SignatureMethod = z.infer<typeof SignatureMethod>

/** Signature artifact class — PRD §4.4. */
export const SignatureType = z.enum(['signature', 'initials'])
export type SignatureType = z.infer<typeof SignatureType>

/** Local analytics event source — PRD §12.1. */
export const AnalyticsSource = z.enum(['local', 'imported', 'remote_sync'])
export type AnalyticsSource = z.infer<typeof AnalyticsSource>

/** Purchase provider — PRD §12.2. */
export const PurchaseProvider = z.enum(['apple', 'google_play', 'stripe'])
export type PurchaseProvider = z.infer<typeof PurchaseProvider>

/** Purchase product class — PRD §12.2. */
export const PurchaseType = z.enum(['lifetime', 'credit_pack'])
export type PurchaseType = z.infer<typeof PurchaseType>

/** Identity provider — PRD §12.2. */
export const IdentityProvider = z.enum(['apple', 'google', 'email'])
export type IdentityProvider = z.infer<typeof IdentityProvider>

/** Storage destination kind — PRD §12.6. */
export const StorageDestinationKind = z.enum(['system_folder', 'download', 'direct_oauth_future'])
export type StorageDestinationKind = z.infer<typeof StorageDestinationKind>

/** Backup snapshot file role — PRD §12.6. */
export const BackupFileRole = z.enum(['current', 'previous', 'manual'])
export type BackupFileRole = z.infer<typeof BackupFileRole>

/** Backup snapshot status — PRD §12.6. */
export const BackupSnapshotStatus = z.enum(['pending', 'writing', 'verified', 'failed'])
export type BackupSnapshotStatus = z.infer<typeof BackupSnapshotStatus>

/** Share observation types — PRD §12.6. Never delivery proof. */
export const ShareObservationType = z.enum([
  'share_requested',
  'share_sheet_opened',
  'link_copied',
  'qr_shown',
  'handoff_observed',
])
export type ShareObservationType = z.infer<typeof ShareObservationType>

/** Share observation authority — PRD §12.6. */
export const ShareAuthority = z.enum(['local_intent', 'platform'])
export type ShareAuthority = z.infer<typeof ShareAuthority>

/** Identity link status — PRD §12.2. */
export const UserStatus = z.enum(['active', 'deleted'])
export type UserStatus = z.infer<typeof UserStatus>

/** Credit ledger reasons (subset used by domain arithmetic) — PRD §12.2/§12.4. */
export const LedgerReason = z.enum([
  'lifetime_grant',
  'credit_pack_grant',
  'managed_envelope',
  'reservation_release',
  'compensate_debit',
  'refund',
  'reversal',
])
export type LedgerReason = z.infer<typeof LedgerReason>

/** Envelope artifact class after completion — PRD §12.2. */
export const ArtifactClass = z.enum(['final_pdf', 'evidence_bundle'])
export type ArtifactClass = z.infer<typeof ArtifactClass>

/**
 * What the provider last reported about a checkout context — S5.3 (§12.6, §2.8).
 *
 * This is the provider's own answer, mirroring the provider seam's `ProviderTransactionState`
 * plus the two states that exist before any session does (`NOT_STARTED`) and after a lapsed one
 * (`UNKNOWN`). It is never a claim of entitlement: the reconciliation class beside it is what the
 * client may act on.
 */
export const CheckoutProviderState = z.enum([
  'NOT_STARTED',
  'OPEN',
  'CANCELED',
  'COMPLETED',
  'REVOKED',
  'UNKNOWN',
])
export type CheckoutProviderState = z.infer<typeof CheckoutProviderState>

/**
 * What a returned handoff reconciles to — S5.3 (IDN-10, PUR-16, OP-022).
 *
 * `SUCCESS` is reachable **only** through a verified entitlement, so a redirect, a thank-you
 * page or an abandoned tab can never produce it. `CANCELED` means the eligible Free/local path is
 * exactly as available as it was before the attempt (ADD-050) and `UNRESOLVED` claims nothing in
 * either direction.
 */
export const CheckoutReconciliationState = z.enum(['PENDING', 'CANCELED', 'SUCCESS', 'UNRESOLVED'])
export type CheckoutReconciliationState = z.infer<typeof CheckoutReconciliationState>

/**
 * Why an unresolved handoff claims nothing — S5.3 (PUR-16). Every member means "the service
 * cannot verify an entitlement here", never "assume paid".
 */
export const CheckoutUnresolvedReason = z.enum([
  'PROVIDER_STATE_UNKNOWN',
  'PROVIDER_STATE_REVOKED',
  'ENTITLEMENT_NOT_VERIFIED',
])
export type CheckoutUnresolvedReason = z.infer<typeof CheckoutUnresolvedReason>

/** Where a returned handoff resumes — S5.3 (§2.8, ADD-050, IDN-10). */
export const CheckoutReturnAction = z.enum(['resume_draft', 'open_purchases', 'none'])
export type CheckoutReturnAction = z.infer<typeof CheckoutReturnAction>
